import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import dayjs from 'dayjs';
import { IInsightSummary } from './IInsight';
import { insightTypeMeta } from './insightTypeMeta';

const formatDate = (date: string) => dayjs(date).format('MM/DD/YY');

const useStyles = createUseStyles({
    eyebrow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap' as const,
    },
    // tinted type pill: icon + small-caps label in the type color, on a 10%-opacity wash of it
    type: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em',
        borderRadius: 999,
        padding: '3px 9px',
    },
    // pushed right rather than laid out with space-between, so a caller can put something ahead of
    // the pill without the date drifting into the middle
    date: {
        fontSize: 12,
        color: 'var(--ion-color-step-600)',
        flexShrink: 0,
        marginLeft: 'auto',
    },
    // Size is inherited so the same block can read as a home row or as a smaller line nested under a
    // client. Weight matches the Recent Summaries title (CareMapNotesListTitle uses 500) so the
    // surfaces read as one system.
    title: {
        fontWeight: 500,
        lineHeight: 1.4,
        // Space under the type pill: 18px on the home card's 16px title, in em so it scales with the
        // smaller line under a client.
        marginTop: '1.125em',
    },
});

/**
 * An insight reduced to what a reader needs in order to decide whether to open it: what kind of
 * finding it is, the window it covers, and its title. The narrative is deliberately absent. These
 * are read in stacks, and a paragraph each turns a list meant for scanning into one that has to be
 * read in full.
 *
 * Presentation only. The wrapper that makes it clickable belongs to the caller, because the same
 * block sits inside an ion-item on home and inside a plain row under a client on the caseload.
 */
export const InsightSummaryContent: FC<{ insight: IInsightSummary }> = ({ insight }) => {
    const classes = useStyles();
    const { label, Icon, color } = insightTypeMeta(insight.type);

    return (
        <>
            <div className={classes.eyebrow}>
                <span className={classes.type} style={{ color, backgroundColor: `${color}1A` }}>
                    <Icon color="inherit" size="small" />
                    {label}
                </span>
                <span className={classes.date}>{formatDate(insight.dateRangeFrom)} – {formatDate(insight.dateRangeTo)}</span>
            </div>
            <div className={`${classes.title} mp-mask`}>{insight.title}</div>
        </>
    );
};
